/** @jsxImportSource @emotion/react */
import { ExclamationCircleOutlined } from '@ant-design/icons'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { getErrorMessageFromFormError } from '@sportninja/common/utils/utils'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  deleteFacility,
  readCountries,
  readProvinces,
} from 'src/helpers/EntityCreationHelper'
import { Button } from '../Button'
import Icon from '../Icon'
import { Input } from '../Input'
import { CountryAndProvinceInput } from '../ts-components/CountryAndProvinceInput'

type InputFields = {
  name: string
  address_line1: string
  address_line2: string
  city: string
  postcode: string
  country: string
  state: string
}

type ReturnArgs = InputFields & {
  name: string
  entityId: string
  entityType: string
  selectedVenue: any
}

type Facility = {
  id?: string
  name: string
}

interface Props {
  initialState?: ReturnArgs
  initialFacilities?: Facility[]
  onCancel: () => void
  onSubmit: any
}

export const NewLocationAddressSelector = ({
  onCancel,
  onSubmit,
  initialState,
  initialFacilities = [],
}: Props) => {
  console.log('🔄 NewLocationAddressSelector: Rendering component', {
    initialState,
  })

  const [error, setError] = useState<string | null>(null)
  const [countriesData, setCountriesData] = useState([]) // Raw country data with id and iso_3166_2
  const [provincesData, setProvincesData] = useState([]) // Raw province data for mapping
  const [value, setValue] = useState(initialState?.name || '')
  const [newFacilityName, setNewFacilityName] = useState('')
  const [facilities, setFacilities] = useState<Facility[]>([])
  const [showNewFacilityInput, setShowNewFacilityInput] = useState(false)
  const [editFacility, setEditFacility] = useState(false)
  const [facilityToEdit, setFacilityToEdit] = useState<Facility>({
    name: '',
    id: undefined,
  })
  const [deletedFacilities, setDeletedFacilities] = useState([])

  // Form values for country and province (using iso_3166_2 codes)
  const [formCountryValue, setFormCountryValue] = useState<string | undefined>(
    undefined
  )
  const [formProvinceValue, setFormProvinceValue] = useState<
    string | undefined
  >(undefined)

  // Helper function to get country ID from iso_3166_2 code
  const getCountryIdFromIso = useMemo(() => {
    return (isoCode: string | undefined): number | undefined => {
      if (!isoCode || !countriesData.length) {
        return undefined
      }
      const country: any = countriesData.find(
        (c: any) => c.iso_3166_2 === isoCode
      )
      console.log(
        '🗺️ getCountryIdFromIso: mapping',
        isoCode,
        'to ID',
        country?.id
      )
      return country?.id
    }
  }, [countriesData])

  // Helper function to get province ID from iso_3166_2 code
  const getProvinceIdFromIso = useMemo(() => {
    return (isoCode: string | undefined): number | undefined => {
      if (!isoCode || !provincesData.length) {
        return undefined
      }
      const province: any = provincesData.find(
        (p: any) => p.iso_3166_2 === isoCode
      )
      console.log(
        '🗺️ getProvinceIdFromIso: mapping',
        isoCode,
        'to ID',
        province?.id
      )
      return province?.id
    }
  }, [provincesData])

  // Check if country requires province (Canada=36, US=226)
  const countryRequiresProvince = useMemo(() => {
    const countryId = getCountryIdFromIso(formCountryValue)
    return countryId === 36 || countryId === 226
  }, [formCountryValue, getCountryIdFromIso])

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel()
      }
    }
    window.addEventListener('keydown', handleEsc)
    return () => {
      window.removeEventListener('keydown', handleEsc)
    }
  }, [onCancel])

  useEffect(() => {
    if (error) {
      const timeout = setTimeout(() => {
        setError(null)
      }, 5000)
      return () => clearTimeout(timeout)
    }
  }, [error])

  // Fetch provinces when country changes
  useEffect(() => {
    const countryId = getCountryIdFromIso(formCountryValue)
    console.log(
      '🔄 NewLocationAddressSelector: Fetching provinces for country ID',
      countryId
    )

    const read = async () => {
      const result = await readProvinces(countryId?.toString() || '')
      if (result) {
        // Store raw province data for mapping
        setProvincesData(result)

        if (initialState?.state) {
          const initialProvince = result.find((c) => {
            const provinceName = c.name.toLowerCase()
            return provinceName === initialState?.state.toLowerCase()
          })
          if (initialProvince) {
            setFormProvinceValue(initialProvince?.iso_3166_2)
          }
        }
      }
    }
    if (countryId) {
      read()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formCountryValue, getCountryIdFromIso])

  // Fetch countries on mount
  useEffect(() => {
    const read = async () => {
      console.log('🔄 NewLocationAddressSelector: Fetching countries')
      const result = await readCountries()
      if (result) {
        const sortedResult = result.sort((a, b) => {
          if (a.id === 36 || a.id === 226) {
            return -1
          } else if (b.id === 36 || b.id === 226) {
            return 1
          } else {
            return 0
          }
        })
        // Store raw country data with id and iso_3166_2
        setCountriesData(sortedResult)

        if (initialState?.country) {
          const initialCountry = result.find((c) => {
            const countryName = c.name.toLowerCase()
            return countryName === initialState?.country.toLowerCase()
          })
          if (initialCountry) {
            setFormCountryValue(initialCountry?.iso_3166_2)
            readProvinces(initialCountry?.id)
          }
        }
      }
    }
    read()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (initialFacilities?.length > 0) {
      initialFacilities.forEach((facility) => {
        setFacilities((prev) => [...prev, facility])
      })
    }
  }, [initialFacilities])

  const {
    control,
    setValue: setFormValue,
    handleSubmit,
  } = useForm({
    defaultValues: {
      address_line1: initialState?.address_line1 || '',
      address_line2: initialState?.address_line2 || '',
      city: initialState?.city || '',
      country: initialState?.country || '',
      postcode: initialState?.postcode || '',
      state: initialState?.state || '',
      name: initialState?.name || '',
    },
  })

  const handleCountryChange = (isoCode: string | undefined) => {
    console.log('🔄 NewLocationAddressSelector: Country changed to', isoCode)
    setFormCountryValue(isoCode)

    // Clear province when country changes
    setFormProvinceValue(undefined)

    // Map to country name for form
    if (isoCode) {
      const country: any = countriesData.find(
        (c: any) => c.iso_3166_2 === isoCode
      )
      if (country) {
        setFormValue('country', country.name)
      }
    } else {
      setFormValue('country', '')
    }
  }

  const handleProvinceChange = (isoCode: string | undefined) => {
    console.log('🔄 NewLocationAddressSelector: Province changed to', isoCode)
    setFormProvinceValue(isoCode)

    // Map to province name for form
    if (isoCode) {
      const province: any = provincesData.find(
        (p: any) => p.iso_3166_2 === isoCode
      )
      if (province) {
        setFormValue('state', province.name)
      }
    } else {
      setFormValue('state', '')
    }
  }

  const onSubmitPressed = async (data: InputFields) => {
    console.log('🔄 NewLocationAddressSelector: Form submitted', data)

    if (!data?.name) {
      setError('Name is required')
      return
    }
    if (!data?.address_line1) {
      setError('Address line 1 is required')
      return
    }
    if (!data?.city) {
      setError('City is required')
      return
    }
    if (!data?.postcode) {
      setError('Postcode is required')
      return
    }
    if (!formCountryValue) {
      setError('Country is required')
      return
    }
    if (countryRequiresProvince) {
      if (!formProvinceValue) {
        setError('Province is required')
        return
      }
    }

    // Map iso codes back to IDs for API submission
    const selectedCountryId = getCountryIdFromIso(formCountryValue)
    const selectedProvinceId = getProvinceIdFromIso(formProvinceValue)

    // Get country and province objects for coordinate lookup
    const country: any = countriesData.find(
      (c: any) => c.iso_3166_2 === formCountryValue
    )
    const province: any = provincesData.find(
      (p: any) => p.iso_3166_2 === formProvinceValue
    )

    const countryObject = {
      id: country?.id,
      name_full: country?.name,
    }
    const provinceObject = province
      ? {
          id: province?.id,
          name: province?.name,
        }
      : undefined

    console.log('🔄 NewLocationAddressSelector: Getting coordinates', {
      street_1: data?.address_line1,
      city: data?.city,
      province: provinceObject,
      country: countryObject,
    })

    const dataToSubmit = {
      name: data?.name,
      name_full: data?.name,
      address: {
        street_1: data?.address_line1,
        street_2: data?.address_line2,
        city: data?.city,
        postal_code: data?.postcode,
        country_id: selectedCountryId,
        province_id: selectedProvinceId,
        facilities: [],
        settings: {},
        address_type_id: 5,
      },
    }
    let facilitiesToSubmit: any[] = []
    if (showNewFacilityInput) {
      facilitiesToSubmit = [...facilities, { name: newFacilityName, id: '' }]
    } else {
      facilitiesToSubmit = facilities
    }
    if (deletedFacilities.length > 0) {
      try {
        await Promise.all(
          deletedFacilities.map((facilityId) => {
            return deleteFacility({
              entityId: initialState?.entityId || '',
              entityType: initialState?.entityType || '',
              facilityId: facilityId,
              venueId: initialState?.selectedVenue?.id,
            })
          })
        )
      } catch (e) {
        console.error(e)
      }
    }
    if (!initialState?.entityId) {
      onSubmit(dataToSubmit, facilitiesToSubmit)
    } else {
      onSubmit(
        dataToSubmit,
        facilitiesToSubmit,
        initialState?.entityId,
        initialState?.entityType,
        initialState?.selectedVenue
      )
    }
  }

  const onError = (error) => {
    const errorMessage = getErrorMessageFromFormError(error)
    setError(errorMessage?.replace('Address_line1', 'Address line 1'))
  }

  const onCancelPressed = () => {
    if (value && !initialState) {
      setValue('')
      setFormValue('name', '')
      setFormValue('address_line1', '')
      setFormValue('address_line2', '')
      setFormValue('city', '')
      setFormValue('country', '')
      setFormValue('postcode', '')
      setFormValue('state', '')
    } else {
      onCancel()
    }
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmitPressed, onError)}
      css={css`
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        flex: 1;
      `}
    >
      <div
        css={css`
          display: flex;
          flex: 1;
          flex-direction: column;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
            margin-bottom: 8px;
          `}
        >
          <Input
            fieldName='name'
            control={control}
            label='Name'
            tabIndex={1}
            required={true}
          />
        </div>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
            margin-bottom: 8px;
          `}
        >
          <Input
            fieldName='address_line1'
            control={control}
            label='Address Line 1'
            tabIndex={1}
            required={true}
          />
          <Input
            fieldName='address_line2'
            control={control}
            label='Address Line 2'
            tabIndex={2}
          />
        </div>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
            margin-bottom: 8px;
          `}
        >
          <Input
            fieldName='city'
            control={control}
            label='City'
            tabIndex={3}
            required={true}
          />
          <Input
            fieldName='postcode'
            control={control}
            label='Postal Code'
            tabIndex={4}
            required={true}
          />
        </div>
        <CountryAndProvinceInput
          countryValue={formCountryValue}
          provinceValue={formProvinceValue}
          onCountryChange={handleCountryChange}
          onProvinceChange={handleProvinceChange}
          valueMode='iso_code'
          provinceValueMode='iso_code'
          fieldErrors={{}}
          requiredFields={{
            country: true,
            province: countryRequiresProvince,
          }}
          disabled={false}
          allowClear={true}
          customCountryList={countriesData?.map(
            (c: any) =>
              ({
                id: c?.id,
                name: c?.name,
                name_full: c.name,
                iso_3166_2: c.iso_3166_2,
              } as any)
          )}
          responsive={true}
          containerStyle={css`
            margin-bottom: 8px;
          `}
        />
      </div>
      <div
        css={css`
          padding: 16px 0;
          border-top: 1px solid ${colors.SOFT_STEEL};
          margin-top: 16px;
          border-bottom: 1px solid ${colors.SOFT_STEEL};
          margin-bottom: 16px;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          {facilities.map((item, index) => (
            <div
              key={index?.toString()}
              css={css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: ${editFacility && facilityToEdit.id === item.id
                  ? '8px 0'
                  : '8px'};
                border-radius: 4px;
                background-color: ${index % 2 === 0
                  ? 'transparent'
                  : editFacility && facilityToEdit.id === item.id
                  ? 'transparent'
                  : colors.HEADER};
              `}
            >
              {editFacility && facilityToEdit.id === item.id ? (
                <div
                  css={css`
                    display: flex;
                    flex: 1;
                    align-items: center;
                    justify-content: space-between;
                    border-width: 1px;
                    border-style: solid;
                    border-color: ${colors.SOFT_STEEL};
                    border-radius: 4px;
                    padding: 8px;
                    max-height: 40px;
                    transition: all 0.1s ease-in-out;

                    &:focus-within {
                      border-color: ${colors.WHITE};
                    }
                  `}
                >
                  <input
                    css={css`
                      display: flex;
                      border: none;
                      width: 100%;
                      flex: 1;
                      font-weight: 500;
                      font-size: 16px;
                      line-height: 16px;
                      color: ${colors.WHITE};
                      background-color: transparent;
                    `}
                    name='facilities-input-creator'
                    type='text'
                    value={facilityToEdit.name}
                    onBlur={() => {
                      setEditFacility(false)
                      setFacilityToEdit({
                        name: '',
                        id: undefined,
                      })
                    }}
                    placeholder='Enter the facility name'
                    onChange={(e) =>
                      setFacilityToEdit({
                        ...facilityToEdit,
                        name: e.target.value,
                      })
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()

                        const newFacilitiesArray = facilities.filter(
                          (_, i) => i !== index
                        )
                        newFacilitiesArray.push(facilityToEdit)
                        setEditFacility(false)
                        setFacilities(newFacilitiesArray)
                        setFacilityToEdit({
                          name: '',
                          id: undefined,
                        })
                      }

                      if (e.key === 'Escape') {
                        setEditFacility(false)
                        setFacilityToEdit({
                          name: '',
                          id: undefined,
                        })
                      }
                    }}
                  />
                  <button
                    onClick={(e) => {
                      e.preventDefault()

                      setEditFacility(false)
                      setFacilityToEdit({
                        name: '',
                        id: undefined,
                      })
                    }}
                  >
                    <Icon
                      name='times'
                      color={colors.DEFAULT_FLAIR}
                      fontSize={18}
                    />
                  </button>
                </div>
              ) : (
                <>
                  <div
                    onClick={() => {
                      setFacilityToEdit(item)
                      setEditFacility(true)
                    }}
                    css={css`
                      display: flex;
                      flex: 1;
                      padding: 8px 0;
                    `}
                  >
                    <span>{item.name}</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault()

                      setFacilities((prev) => {
                        const facility: any = prev.find((_, i) => i === index)
                        if (facility?.id) {
                          setDeletedFacilities(
                            (p: any) => [...p, facility.id] as any
                          )
                        }
                        return prev.filter((_, i) => i !== index)
                      })
                    }}
                  >
                    <Icon name='trash' color={colors.DEFAULT_FLAIR} />
                  </button>
                </>
              )}
            </div>
          ))}
          {showNewFacilityInput && (
            <div
              css={css`
                display: flex;
                flex: 1;
                align-items: center;
                justify-content: space-between;
                border-width: 1px;
                border-style: solid;
                border-color: ${colors.SOFT_STEEL};
                border-radius: 4px;
                padding: 8px;
                max-height: 40px;
                transition: all 0.1s ease-in-out;
                margin-top: 16px;

                &:focus-within {
                  border-color: ${colors.WHITE};
                }
              `}
            >
              <input
                css={css`
                  display: flex;
                  border: none;
                  width: 100%;
                  flex: 1;
                  font-weight: 500;
                  font-size: 16px;
                  line-height: 16px;
                  color: ${colors.WHITE};
                  background-color: transparent;
                `}
                name='facilities-input-creator'
                type='text'
                value={newFacilityName}
                placeholder='Enter the facility name'
                onChange={(e) => setNewFacilityName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()

                    setShowNewFacilityInput(false)
                    setFacilities((prev) => [
                      ...prev,
                      { name: newFacilityName, id: '' },
                    ])
                    setNewFacilityName('')
                  }
                }}
              />
              <button
                type='reset'
                onClick={(e) => {
                  e.preventDefault()

                  setShowNewFacilityInput(false)
                  setNewFacilityName('')
                }}
              >
                <Icon
                  name='times'
                  color={colors.ATTENDANCE_GRAY}
                  fontSize={18}
                />
              </button>
            </div>
          )}
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: flex-end;
              margin-top: ${facilities.length > 0 || showNewFacilityInput
                ? '16px'
                : '0'};
            `}
          >
            <Button
              onClick={(e) => {
                e.preventDefault()
                if (!showNewFacilityInput) {
                  setShowNewFacilityInput(true)
                } else {
                  if (newFacilityName && newFacilityName.length > 0) {
                    setFacilities((prev) => [
                      ...prev,
                      { name: newFacilityName, id: '' },
                    ])
                    setNewFacilityName('')
                  }
                }
              }}
              label='+ Add Facility'
              variant='secondary'
              // @ts-ignore
              labelStyle={css`
                text-transform: uppercase;
                font-size: 20px;
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
              `}
            />
          </div>
        </div>
      </div>
      {error ? (
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            margin-top: 20px;
          `}
        >
          <ExclamationCircleOutlined
            style={{
              color: colors.FIERY_RED,
              fontSize: 16,
            }}
          />
          <p
            tabIndex={-1}
            css={css`
              font-weight: 500;
              font-size: 14px;
              line-height: 14px;
              color: ${colors.FIERY_RED};
            `}
          >
            {error}
          </p>
        </div>
      ) : null}
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 16px;
          margin-top: 20px;
        `}
      >
        <Button
          onClick={(e) => {
            e.preventDefault()

            onCancelPressed()
          }}
          label={value && !initialState ? 'Clear' : 'Cancel'}
          variant='secondary'
          // @ts-ignore
          labelStyle={css`
            text-transform: uppercase;
            font-size: 20px;
            font-family: ${isCanlan
              ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
              : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
          `}
        />
        <Button
          id='submit'
          onClick={handleSubmit(onSubmitPressed, onError)}
          type='submit'
          label='Submit'
          // @ts-ignore
          labelStyle={css`
            text-transform: uppercase;
            font-size: 20px;
            font-family: ${isCanlan
              ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
              : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
          `}
        />
      </div>
    </form>
  )
}
